import { useForm } from 'react-hook-form';
import { API } from "../utils/amplifyWrapper";
import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { Camera } from 'lucide-react';

import Spinner from '../components/Spinner';

import PODCAST_CATEGORIES from '../constants/podcast_categories.json';
import clean from '../utils/Clean';
import { useAddEpisode, useUpdateEpisodeSettings } from '../http-hooks/podcast/usePodcast';

function PodcastEpisodeSettings({ show, onHide, orgId, integrationId, podcastId, isNew, selectedEpisode, onSuccess = () => { } }) {

    const { register, reset, formState: { errors }, handleSubmit } = useForm();
    const [episodeArtFile, setEpisodeArtFile] = useState(null);
    const [isSavingEpisodeSettings, setIsSavingEpisodeSettings] = useState(false);
    const history = useHistory();
    
    // React Query hooks
    const addEpisodeMutation = useAddEpisode({ 
        org_id: orgId, 
        integration_id: integrationId, 
        podcast_id: podcastId 
    });
    const updateEpisodeSettingsMutation = useUpdateEpisodeSettings({ 
        org_id: orgId, 
        integration_id: integrationId, 
        podcast_id: podcastId,
        content_id: selectedEpisode?.content_id 
    });

    const onSaveEpisodeSettings = async (data) => {

        setIsSavingEpisodeSettings(true);

        if (episodeArtFile !== null) {
            const signedResponse = await API.get(`main`, `/episode/art/signed-url`, {
                queryStringParameters: {
                    file_name: episodeArtFile.name,
                    content_type: episodeArtFile.type
                }
            });

            const requestOptions = {
                method: 'PUT',
                body: episodeArtFile,
                redirect: 'follow'
            };

            const uploadURL = signedResponse.data.signed_url;
            const url = signedResponse.data.url;

            await fetch(uploadURL, requestOptions);

            data.episode_art = url;
        }

        try {
            if (isNew) {
                // Add episode - use React Query mutation
                await addEpisodeMutation.mutateAsync({
                    content_id: selectedEpisode.content_id,
                    ...clean(data)
                });
                onSuccess(data);
                onHide();
                history.push(`/organisation/${orgId}/integration/${integrationId}/podcast/${podcastId}/episodes`);
                setEpisodeArtFile(null);
            } else {
                // Update episode - use React Query mutation
                await updateEpisodeSettingsMutation.mutateAsync(clean(data));
                onSuccess(data);
                onHide();
                setEpisodeArtFile(null);
            }
        } catch (error) {
            // Error handling is done in the hooks
            console.error('Episode settings error:', error);
        } finally {
            setIsSavingEpisodeSettings(false);
        }

    }

    const PodcastPosterImagePicker = () => {
        return (
            <>
                <div className="poster-container episode-art-container d-flex align-items-center justify-content-center mx-auto mx-xl-0 position-relative mb-3">
                    <input type="file" id="poster" onChange={(event) => { setEpisodeArtFile(event.target.files[0]) }} />
                    <label htmlFor="poster" className="position-absolute">
                        {
                            (episodeArtFile !== null)
                                ? (
                                    <img className="episode-image" alt="poster" src={URL.createObjectURL(episodeArtFile)} />
                                )
                                : (selectedEpisode?.episode_art)
                                    ? (
                                        <img className="episode-image" alt="poster" src={selectedEpisode.episode_art} />
                                    ) : (
                                        <Camera size={64} color="var(--bs-secondary-color)" strokeWidth={1} />
                                    )
                        }
                    </label>
                </div>
            </>
        );
    };

    useEffect(() => {

        if (selectedEpisode && show) {
            reset({
                episode_title: selectedEpisode.episode_title || selectedEpisode.title,
                episode_description: selectedEpisode.episode_description || selectedEpisode.description || selectedEpisode.body,
                episode_author: selectedEpisode.episode_author || selectedEpisode.author,
                episode_category: selectedEpisode.episode_category || selectedEpisode.category,
                is_published: selectedEpisode.is_published !== undefined ? selectedEpisode.is_published : true
            });
            // Reset the episode art file when switching episodes
            setEpisodeArtFile(null);
        }

    }, [selectedEpisode, show, reset]);

    return (
        <Modal 
            className="modal-playlist-code" 
            show={show} 
            onHide={onHide}
            key={selectedEpisode?.content_id || 'new-episode'}
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    Episode Settings
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="container">
                <div className="row">
                    <div className="col-12 col-md-4">
                        <PodcastPosterImagePicker />
                        <p>The image should be square and atleast 1400x1400 pixels in size. We support images in jpg, png, gif and tiff formats up to a maximum of 4MB in size.</p>
                    </div>
                    <form className="col-12 col-md-8 mb-4">
                        <div className="mb-3">
                            <label>Episode Title</label>
                            <input type="text" className={classNames("form-control", { "is-invalid": errors.title })} {...register("episode_title", { required: true, minLength: 3 })} />
                            {
                                errors.title &&
                                <div className="invalid-feedback">
                                    {
                                        ("required" === errors.title.type) && "Podcast title field is required."
                                    }
                                    {
                                        ("minLength" === errors.title.type) && "Podcast Title field should be at least 3 character long."
                                    }
                                </div>
                            }
                        </div>
                        <div className="mb-3">
                            <label>Episode Description</label>
                            <textarea className={classNames("form-control", { "is-invalid": errors.body })} rows="10" {...register("episode_description")} />
                            {
                                errors.body &&
                                <div className="invalid-feedback">
                                    {
                                        ("required" === errors.body.type) && "Text field is required."
                                    }
                                    {
                                        ("minLength" === errors.body.type) && "Text field should be at least 3 character long."
                                    }
                                </div>
                            }
                        </div>
                        <div className="mb-3">
                            <label>Episode Host</label>
                            <input type="text" className={classNames("form-control", { "is-invalid": errors.title })} {...register("episode_author", { required: true, minLength: 3 })} />
                            {
                                errors.title &&
                                <div className="invalid-feedback">
                                    {
                                        ("required" === errors.title.type) && "Podcast title field is required."
                                    }
                                    {
                                        ("minLength" === errors.title.type) && "Podcast Title field should be at least 3 character long."
                                    }
                                </div>
                            }
                        </div>
                        <div className="mb-3">
                            <label>Category</label>
                            <select className={classNames("form-select", { "is-invalid": errors.category })}  {...register("episode_category", { required: true })} >
                                {
                                    Object.keys(PODCAST_CATEGORIES).map((category, i) => {
                                        return (
                                            <option value={category}>{category}</option>
                                        )
                                    })
                                }
                            </select>
                            {
                                errors.category &&
                                <div className="invalid-feedback">
                                    {
                                        ("required" === errors.category.type) && "Category field is required."
                                    }
                                </div>
                            }
                        </div>
                        <div class="form-check form-switch">
                            <input type="checkbox" class="form-check-input" id="is_published"  {...register("is_published")} />
                            <label class="form-check-label" for="is_published">Publish this Episode?</label>
                        </div>
                    </form>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-outline-primary me-3" onClick={onHide}>Cancel</button>
                {
                    (isSavingEpisodeSettings) ?
                        (
                            <button type="button" className="btn btn-primary text-center">
                                <Spinner color="white" />
                            </button>
                        )
                        :
                        (
                            <button type="button" className="btn btn-primary" onClick={handleSubmit(onSaveEpisodeSettings)}>Save</button>
                        )
                }
            </Modal.Footer>
        </Modal>
    );
}

export default PodcastEpisodeSettings;
